/*
  Purekey: vodoravni drsnik s puščicama (PLAN 3: UGC, ocene).
  Označevanje: <pk-carousel> z [data-pk-track] (vodoravno drsenje s scroll-snap) ter neobveznima
  gumboma [data-pk-prev] in [data-pk-next]. Gumba sta onemogočena na začetku in koncu.
  V urejevalniku teme izbira bloka pomakne drsnik do bloka.
  Vlečenje z miško: med vlečenjem je scroll-snap izklopljen, ob spustu drsnik z animacijo zdrsne do
  najbližje ploščice (zamah upošteva hitrost) in šele nato se snap vrne, da ne skoči.
*/
if (!customElements.get('pk-carousel')) {
  class PkCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-pk-track]');
      if (!this.track) return;

      if (!this.initialized) {
        this.initialized = true;
        this.prev = this.querySelector('[data-pk-prev]');
        this.next = this.querySelector('[data-pk-next]');
        this.frame = null;
        this.settleFrame = null;

        this.scheduleUpdate = () => {
          if (this.frame) return;
          this.frame = window.requestAnimationFrame(() => {
            this.frame = null;
            this.update();
          });
        };

        this.track.addEventListener('scroll', this.scheduleUpdate, { passive: true });
        this.prev?.addEventListener('click', () => this.go(-1));
        this.next?.addEventListener('click', () => this.go(1));

        this.onBlockSelect = (event) => {
          if (!this.track.contains(event.target)) return;
          event.target.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
        };

        this.setupDrag();
      }

      this.resizeObserver = new ResizeObserver(this.scheduleUpdate);
      this.resizeObserver.observe(this.track);
      document.addEventListener('shopify:block:select', this.onBlockSelect);
      this.update();
    }

    disconnectedCallback() {
      this.resizeObserver?.disconnect();
      this.stopSettle();
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
    }

    /*
      Vlečenje z miško (na telefonu so puščice lahko skrite, dotik pa ima naravno drsenje brskalnika).
      Klik po vlečenju ne sme sprožiti videa, zato ga v fazi zajema ustavimo.
    */
    setupDrag() {
      let pointerId = null;
      let startX = 0;
      let startScroll = 0;
      let lastX = 0;
      let lastTime = 0;
      let velocity = 0;
      this.dragged = false;

      this.track.addEventListener('pointerdown', (event) => {
        if (event.pointerType === 'touch' || event.button !== 0) return;
        this.stopSettle();
        pointerId = event.pointerId;
        startX = event.clientX;
        lastX = event.clientX;
        lastTime = event.timeStamp;
        velocity = 0;
        startScroll = this.track.scrollLeft;
        this.dragged = false;
      });

      this.track.addEventListener('pointermove', (event) => {
        if (pointerId === null || event.pointerId !== pointerId) return;
        const distance = event.clientX - startX;
        if (!this.dragged) {
          if (Math.abs(distance) < 5) return;
          this.dragged = true;
          this.track.setPointerCapture?.(pointerId);
          this.track.style.scrollSnapType = 'none';
          this.classList.add('is-dragging');
        }

        const elapsed = event.timeStamp - lastTime;
        if (elapsed > 0) {
          // Utežena hitrost (px/ms), da posamezen sunek ne prevlada
          velocity = 0.7 * ((event.clientX - lastX) / elapsed) + 0.3 * velocity;
          lastX = event.clientX;
          lastTime = event.timeStamp;
        }

        this.track.scrollLeft = startScroll - distance;
        event.preventDefault();
      });

      const endDrag = (event) => {
        if (pointerId === null || (event.pointerId !== undefined && event.pointerId !== pointerId)) return;
        const id = pointerId;
        pointerId = null;
        if (this.track.style.scrollSnapType !== 'none') return;
        if (this.dragged) this.track.releasePointerCapture?.(id);
        this.classList.remove('is-dragging');
        this.settle(this.dragged ? velocity : 0);
      };

      this.track.addEventListener('pointerup', endDrag);
      this.track.addEventListener('pointercancel', endDrag);
      this.track.addEventListener('dragstart', (event) => {
        if (this.dragged) event.preventDefault();
      });

      this.track.addEventListener('click', (event) => {
        if (!this.dragged) return;
        event.preventDefault();
        event.stopPropagation();
        this.dragged = false;
      }, true);
    }

    /* Mesto najbližje ploščice glede na podan odmik (upošteva scroll-padding drsnika) */
    nearest(position) {
      const max = Math.max(this.track.scrollWidth - this.track.clientWidth, 0);
      const wanted = Math.min(Math.max(position, 0), max);
      const trackLeft = this.track.getBoundingClientRect().left;
      const padStart = parseFloat(window.getComputedStyle(this.track).scrollPaddingInlineStart) || 0;
      const current = this.track.scrollLeft;
      let best = wanted;
      let bestDistance = Infinity;

      for (const item of this.track.children) {
        const offset = item.getBoundingClientRect().left - trackLeft - padStart;
        const candidate = Math.min(Math.max(current + offset, 0), max);
        const distance = Math.abs(candidate - wanted);
        if (distance < bestDistance) {
          bestDistance = distance;
          best = candidate;
        }
      }

      return best;
    }

    /* Mehko ustavljanje po vlečenju: zamah nese naprej, nato animacija do ploščice in vrnjen snap */
    settle(velocity = 0) {
      const target = this.nearest(this.track.scrollLeft - velocity * 120);
      const start = this.track.scrollLeft;
      const distance = target - start;
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      if (reduceMotion || Math.abs(distance) < 1) {
        this.track.scrollLeft = target;
        this.restoreSnap();
        return;
      }

      const duration = Math.min(560, Math.max(260, Math.abs(distance) * 1.2));
      const startTime = performance.now();
      const step = (now) => {
        const progress = Math.min((now - startTime) / duration, 1);
        const eased = 1 - Math.pow(1 - progress, 3);
        this.track.scrollLeft = start + distance * eased;
        if (progress < 1) {
          this.settleFrame = window.requestAnimationFrame(step);
          return;
        }
        this.settleFrame = null;
        this.restoreSnap();
      };

      this.settleFrame = window.requestAnimationFrame(step);
    }

    stopSettle() {
      if (!this.settleFrame) return;
      window.cancelAnimationFrame(this.settleFrame);
      this.settleFrame = null;
    }

    restoreSnap() {
      this.track.style.scrollSnapType = '';
    }

    step() {
      const item = this.track.firstElementChild;
      if (!item) return this.track.clientWidth;
      const gap = parseFloat(window.getComputedStyle(this.track).columnGap) || 0;
      return item.getBoundingClientRect().width + gap;
    }

    go(direction) {
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.track.scrollBy({ left: direction * this.step(), behavior: reduceMotion ? 'auto' : 'smooth' });
    }

    update() {
      const max = this.track.scrollWidth - this.track.clientWidth;
      const left = Math.abs(this.track.scrollLeft);
      if (this.prev) this.prev.disabled = left <= 1;
      if (this.next) this.next.disabled = left >= max - 1;
    }
  }

  customElements.define('pk-carousel', PkCarousel);
}
